import { z } from 'zod';
import { PlayerIdSchema } from '../ids';
import { CoalitionTargetSchema } from './coalition';
import { HolderNameSchema } from './holder-name';
import { ObjectViewSchema } from './inventory';
import { ItemAmountsSchema } from './items';
import { PowerIdSchema } from './reputation';

/**
 * A quest chain's id (QUEST-CHAIN): a public contract (progress rows, i18n `quests.<id>.*`, the
 * wire): never rename.
 */
export const QuestIdSchema = z.string().regex(/^[a-z][a-z0-9_]{0,47}$/);
export type QuestId = z.infer<typeof QuestIdSchema>;

/** Where a character stands with a chain: on it, set aside (resumed where it was left), or done. */
export const QUEST_STATUSES = ['active', 'abandoned', 'done'] as const;
export const QuestStatusSchema = z.enum(QUEST_STATUSES);
export type QuestStatus = z.infer<typeof QuestStatusSchema>;

/** The eight bearings a hint gives (QU-2), from a place the character knows toward the step's. */
export const QUEST_BEARINGS = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'] as const;
export const QuestBearingSchema = z.enum(QUEST_BEARINGS);
export type QuestBearing = z.infer<typeof QuestBearingSchema>;

/**
 * Where a step points (QU-2, OC-2): the place by its name when the character knows it; else a
 * place it knows, the bearing from there and whether it is far. Never a position.
 */
export const QuestHintSchema = z.discriminatedUnion('k', [
  z.object({ k: z.literal('place'), place: z.string() }),
  z.object({
    k: z.literal('near'),
    place: z.string(),
    bearing: QuestBearingSchema,
    far: z.boolean(),
  }),
]);
export type QuestHint = z.infer<typeof QuestHintSchema>;

/**
 * The scopes a zone guide names (QG-1..4): a traced zone (HB-1) holding the place, else its
 * district, else its region: the narrowest the trace has.
 */
export const QUEST_ZONE_SCOPES = ['zone', 'district', 'region'] as const;
export type QuestZoneScope = (typeof QUEST_ZONE_SCOPES)[number];

/**
 * What a step's mark shows on the map (QG-1..4, which corrects QU-2), beside the hint's words:
 * - `point`: a person, a delivery or something to touch, only at a place the character knows;
 * - `area`: a hunt, a gathering or an exploration, a circle whose centre the server shifts by a
 *   seed of its own, so it never gives the target away (OC-2);
 * - `zone`: a place the character does not know, by its zone, its district or its region (`id`,
 *   named by `zones.<id>`, `districts.<id>` or `regions.<id>`).
 */
export const QuestGuideSchema = z.discriminatedUnion('k', [
  z.object({ k: z.literal('point'), x: z.number(), y: z.number() }),
  z.object({ k: z.literal('area'), x: z.number(), y: z.number(), r: z.number().positive() }),
  z.object({ k: z.literal('zone'), scope: z.enum(QUEST_ZONE_SCOPES), id: z.string() }),
]);
export type QuestGuide = z.infer<typeof QuestGuideSchema>;

/**
 * Whom a step names by a role (CREATURE-NAMED, NN-7): the role, and its holder's name while one
 * stands; without `name` the role is empty and the text says the role (NN-8).
 */
export const QuestHolderSchema = z.object({
  role: z.string().min(1),
  name: HolderNameSchema.optional(),
});
export type QuestHolder = z.infer<typeof QuestHolderSchema>;

/**
 * What a chain (or what is left of it) pays, as the journal tells it (DJ-1): experience as its factor
 * (the client only says «experience» when above 0), coins, goods, standing with `power` (who pays,
 * named by the client; QU-6), how many places it teaches (never which), a spell, how many marks.
 */
export const QuestRewardViewSchema = z.object({
  xp: z.number().min(0),
  coins: z.number().int().min(0),
  items: ItemAmountsSchema,
  reputation: z.number().int().min(0),
  power: PowerIdSchema.optional(),
  places: z.number().int().min(0),
  spell: z.literal(true).optional(),
  marks: z.number().int().min(1).optional(),
});
export type QuestRewardView = z.infer<typeof QuestRewardViewSchema>;

/**
 * A way a chain may split into (Q2b) as the journal tells it before it is chosen: its steps and
 * what it pays besides what is certain. Only the ways open to the character travel (OC-2).
 */
export const QuestBranchViewSchema = z.object({
  id: z.string(),
  steps: z.number().int().min(1),
  reward: QuestRewardViewSchema,
});
export type QuestBranchView = z.infer<typeof QuestBranchViewSchema>;

/** A chain in a character's log (QUEST-LOG): its step, how far into it, and where it points. */
export const QuestEntrySchema = z.object({
  id: QuestIdSchema,
  status: QuestStatusSchema,
  step: z.number().int().min(0),
  /** How many of the step's count are done, and its count (a defeat's; one otherwise). */
  count: z.number().int().min(0),
  target: z.number().int().min(1),
  /** Whom its step asks, in any order (RE-4), and who of them has been asked, by giver id. */
  asks: z.array(z.string()).max(6).optional(),
  asked: z.array(z.string()).max(6).optional(),
  /** The branch chosen where the chain splits (Q2b); null before, or with none. */
  branch: z.string().nullable().optional(),
  /** Its step's index within its chosen branch (0 its first), once one is chosen (Q2b). */
  branchStep: z.number().int().min(0).optional(),
  /** While it waits where it splits: its branches, chosen by speaking to their givers. */
  choices: z.array(z.string()).optional(),
  /** Null once done, or when its step has no place to point at. */
  hint: QuestHintSchema.nullable(),
  /** Its step's mark on the map (QG-1..4); null once done, with no place, or none by its author. */
  guide: QuestGuideSchema.nullable().optional(),
  /** The lore campaign's (KM-1), which the journal keeps apart; absent: a region's chain. */
  campaign: z.boolean().optional(),
  /** An origin's prologue (B165), lived in its memory; the journal keeps it apart. */
  prologue: z.literal(true).optional(),
  /**
   * Its step's band is gone while a player holds its place (QUEST, QL-2): reaching it is enough.
   * Absent otherwise.
   */
  held: z.literal(true).optional(),
  /**
   * Whom its step names by a role (CREATURE-NAMED, NN-7): the role, and its holder's name while one
   * stands (its text's `{{holder}}`); without `name` the role is empty and the text says the role
   * (NN-8). Absent: its step names nobody by a role.
   */
  holder: QuestHolderSchema.optional(),
  /** Who gave it (its chosen branch's giver once chosen); absent for a prologue (DJ-1). */
  giver: z.string().optional(),
  /** Its steps known so far: the trunk's, plus its chosen branch's (DJ-1). */
  steps: z.number().int().min(1).optional(),
  /** What is left to be paid on the known way: the steps to come and its end (DJ-1). */
  reward: QuestRewardViewSchema.optional(),
  /** Before it splits: the ways open to it, each with what it adds (DJ-1). */
  branches: z.array(QuestBranchViewSchema).max(4).optional(),
});
export type QuestEntry = z.infer<typeof QuestEntrySchema>;

/**
 * What a contract asks (ECON-CONTRACT; QUEST-BOARD): the config's quest objective, as data the
 * client words (`kind` with its fields: a sheet or family to defeat, items to deliver, a place, a
 * power, a community to feed). Its count is what it asks to count to.
 */
export const ContractObjectiveSchema = z.object({
  kind: z.string(),
  target: z.number().int().min(1),
  place: z.string().optional(),
  sheet: z.string().optional(),
  family: z.string().optional(),
  power: z.string().optional(),
  items: ItemAmountsSchema.optional(),
  /** The living community a `supply` brings food to (LL-4). */
  community: z.string().optional(),
  /** The faction a coalition's bounty hunts, with its name (NPC-KINGDOM-REACT, IK-5). */
  quarry: CoalitionTargetSchema.optional(),
});
export type ContractObjective = z.infer<typeof ContractObjectiveSchema>;

/**
 * What a contract pays each one who completes it, kept from its publication: a character's also
 * unique objects (JC-6), which wait with its goods in the completer's vault at the board (JC-8).
 */
export const ContractRewardSchema = z.object({
  xp: z.number().min(0),
  coins: z.number().int().min(0),
  items: ItemAmountsSchema,
  objects: z.array(ObjectViewSchema).optional(),
  reputation: z.number().int().min(0),
});
export type ContractReward = z.infer<typeof ContractRewardSchema>;

/**
 * A contract as a character sees it on a board or in its log: what it asks and pays, how many
 * slots are left, when it ends, and the character's own take of it (null: not taken). A
 * character's delivery (ECON-CONTRACT C3a) also says who published it and its terms; a power's
 * task leaves those out.
 */
export const ContractViewSchema = z.object({
  id: z.string(),
  board: z.string(),
  power: z.string().nullable(),
  /** The character that published it (C3a), by id and name. */
  issuer: z.object({ playerId: PlayerIdSchema, name: z.string() }).optional(),
  /** One taker at a time, with a term and a guarantee (KT-4). */
  exclusive: z.boolean().optional(),
  /** Coins its taker leaves while on it (KT-3), lost if the term ends first. */
  guarantee: z.number().int().min(0).optional(),
  /** How long its taker has, in game time. */
  termMs: z.number().int().positive().nullable().optional(),
  /** An exclusive one taken by someone (its taker sees it as its own). */
  taken: z.boolean().optional(),
  /** A transport's destination board (C3c), and whether its goods travel sealed already. */
  destination: z.string().optional(),
  sealed: z.boolean().optional(),
  objective: ContractObjectiveSchema,
  reward: ContractRewardSchema,
  slots: z.number().int().min(1),
  left: z.number().int().min(0),
  status: z.enum(['open', 'closed', 'expired']),
  /** Game time it ends at, or null while the world asks for it. */
  expiresAt: z.number().int().nullable(),
  mine: z
    .object({
      status: z.enum(['active', 'done']),
      count: z.number().int().min(0),
      /** Where it points (QU-2), never a position; null once done or with no place. */
      hint: QuestHintSchema.nullable(),
      /** Its mark on the map (QG-1..4): a board's and the world's errands show an area. */
      guide: QuestGuideSchema.nullable().optional(),
      /** What it brought to a delivery so far, its own until the contract is done (C3a). */
      delivered: ItemAmountsSchema.optional(),
      /** When its term ends, on an exclusive one it took (game time). */
      dueAt: z.number().int().nullable().optional(),
    })
    .nullable(),
});
export type ContractView = z.infer<typeof ContractViewSchema>;

/** A character's record as a contractor (KT-3): what it published, completed and failed. */
export const ContractHistorySchema = z.object({
  published: z.number().int().min(0),
  completed: z.number().int().min(0),
  failed: z.number().int().min(0),
});
export type ContractHistory = z.infer<typeof ContractHistorySchema>;

/** A character's log: the chains it is on or set aside, and those it finished. */
/**
 * The campaign's givers some chain needs now for the character (KM-9: Corlo stands only where a
 * moment asks for him); the server alone judges it, the client hides the others.
 */
export const NeededGiversSchema = z
  .array(
    z
      .string()
      .regex(/^[a-z][a-z0-9_]{0,47}$/)
      .max(48),
  )
  .max(32);

export const QuestLogSchema = z.object({
  quests: z.array(QuestEntrySchema),
  done: z.array(QuestIdSchema),
  /** Which of `done` are prologues (B165): the journal hides them among the finished. */
  prologues: z.array(QuestIdSchema).optional(),
  needed: NeededGiversSchema.optional(),
  /** The board contracts it took and is on (QUEST-BOARD), each as the board shows it. */
  contracts: z.array(ContractViewSchema).optional(),
});
export type QuestLog = z.infer<typeof QuestLogSchema>;

/**
 * What a giver offers a character (QUEST-CHAIN): the chains taken there it has not finished, and
 * what each still asks of it (a level, a band with a power, a chain before).
 */
export const QuestOfferSchema = z.object({
  id: QuestIdSchema,
  status: z.enum(['open', 'active', 'abandoned', 'locked']),
  /** Speaking here chooses this branch of the chain (Q2b), when it waits for that choice. */
  choose: z.string().optional(),
  /** Whom that branch's choice names by a role (CREATURE-NAMED), as a log's entry tells it. */
  holder: QuestHolderSchema.optional(),
  /**
   * Speaking here does its step now (an `active` chain's talk, or its delivery, with this giver at
   * its hours): the server's word, so a chain on its way is never offered a talk that only answers
   * «not this step» (the user, 2026-10-20). Absent on an active chain: its step is elsewhere.
   */
  speak: z.literal(true).optional(),
  /** What it lacks, when locked. */
  missing: z
    .object({
      level: z.number().int().optional(),
      band: z.object({ power: z.string(), band: z.string() }).optional(),
      quest: QuestIdSchema.optional(),
    })
    .optional(),
  /** Not taken yet or set aside: its trunk's steps and what is certain to be paid (DJ-1). */
  steps: z.number().int().min(1).optional(),
  reward: QuestRewardViewSchema.optional(),
  /** Where it splits: the ways open to the character, each with what it adds (DJ-1). */
  branches: z.array(QuestBranchViewSchema).max(4).optional(),
  /** What its giver hands over on taking it, never taken before (DB-140): told as a gift. */
  gives: ItemAmountsSchema.optional(),
  /** The lore campaign's (KM-1): its places are told as «something you don't know» (DJ-1). */
  campaign: z.literal(true).optional(),
});
export type QuestOffer = z.infer<typeof QuestOfferSchema>;
